import React, { forwardRef } from 'react';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { cn } from '../../../../utils/cn';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'primary' | 'secondary' | 'outline' | 'ghost' | 'destructive';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  className?: string;
  children: React.ReactNode;
}

const BUTTON_VARIANTS = {
  default: [
    'bg-neutral-900 text-white border-neutral-900',
    'hover:bg-neutral-800 hover:border-neutral-800',
    'focus:ring-neutral-500/20',
    'active:bg-neutral-950'
  ].join(' '),
  
  primary: [
    'bg-primary-600 text-white border-primary-600',
    'hover:bg-primary-700 hover:border-primary-700',
    'focus:ring-primary-500/20',
    'active:bg-primary-800',
    'shadow-sm'
  ].join(' '),
  
  secondary: [
    'bg-secondary-600 text-white border-secondary-600',
    'hover:bg-secondary-700 hover:border-secondary-700',
    'focus:ring-secondary-500/20',
    'active:bg-secondary-800',
    'shadow-sm'
  ].join(' '),
  
  outline: [
    'bg-transparent text-neutral-700 border-neutral-300',
    'hover:bg-neutral-50 hover:border-neutral-400',
    'focus:ring-neutral-500/20',
    'active:bg-neutral-100'
  ].join(' '),
  
  ghost: [
    'bg-transparent text-neutral-700 border-transparent',
    'hover:bg-neutral-100 hover:text-neutral-900',
    'focus:ring-neutral-500/20',
    'active:bg-neutral-200'
  ].join(' '),
  
  destructive: [
    'bg-red-600 text-white border-red-600',
    'hover:bg-red-700 hover:border-red-700',
    'focus:ring-red-500/20',
    'active:bg-red-800',
    'shadow-sm'
  ].join(' ')
};

const BUTTON_SIZES = {
  xs: 'px-2.5 py-1.5 text-xs font-medium rounded-md',
  sm: 'px-3 py-2 text-sm font-medium rounded-lg',
  md: 'px-4 py-2.5 text-sm font-medium rounded-lg',
  lg: 'px-5 py-3 text-base font-medium rounded-lg',
  xl: 'px-6 py-3.5 text-base font-semibold rounded-xl'
};

const ICON_SIZES = {
  xs: 'w-3 h-3',
  sm: 'w-4 h-4',
  md: 'w-4 h-4',
  lg: 'w-5 h-5',
  xl: 'w-5 h-5'
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  variant = 'default',
  size = 'md',
  loading = false,
  leftIcon,
  rightIcon,
  fullWidth = false,
  className,
  disabled,
  children,
  ...props
}, ref) => {
  const isDisabled = disabled || loading;

  return (
    <motion.button
      ref={ref}
      className={cn(
        // Base styles
        'inline-flex items-center justify-center font-medium',
        'border transition-all duration-200',
        'focus:outline-none focus:ring-2 focus:ring-offset-2',
        'disabled:opacity-50 disabled:pointer-events-none',
        'relative overflow-hidden',
        
        // Size styles
        BUTTON_SIZES[size],
        
        // Variant styles
        BUTTON_VARIANTS[variant],
        
        // Full width
        fullWidth && 'w-full',
        
        // Custom className
        className
      )}
      disabled={isDisabled}
      whileHover={!isDisabled ? { scale: 1.02 } : undefined}
      whileTap={!isDisabled ? { scale: 0.98 } : undefined}
      {...(props as any)}
    >
      {/* Loading spinner */}
      {loading && (
        <Loader2 className={cn(
          'animate-spin mr-2',
          ICON_SIZES[size]
        )} />
      )}
      
      {/* Left icon */}
      {!loading && leftIcon && (
        <span className={cn('mr-2', ICON_SIZES[size])}>
          {leftIcon}
        </span>
      )}
      
      {/* Button content */}
      <span className={loading ? 'opacity-70' : ''}>
        {children}
      </span>
      
      {/* Right icon */}
      {!loading && rightIcon && (
        <span className={cn('ml-2', ICON_SIZES[size])}>
          {rightIcon}
        </span>
      )}
      
      {/* Hover effect overlay */}
      <motion.div
        className="absolute inset-0 bg-white/10 opacity-0"
        whileHover={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
      />
    </motion.button>
  );
});

Button.displayName = 'Button';

// Button Group Component
export interface ButtonGroupProps {
  children: React.ReactNode;
  orientation?: 'horizontal' | 'vertical';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'default' | 'primary' | 'secondary' | 'outline' | 'ghost';
  className?: string;
}

export const ButtonGroup: React.FC<ButtonGroupProps> = ({
  children,
  orientation = 'horizontal',
  size = 'md',
  variant = 'default',
  className
}) => {
  return (
    <div
      className={cn(
        'inline-flex',
        orientation === 'horizontal' ? 'flex-row' : 'flex-col',
        '[&>button]:border-r-0 [&>button]:rounded-none',
        '[&>button:first-child]:rounded-l-lg [&>button:last-child]:rounded-r-lg [&>button:last-child]:border-r',
        orientation === 'vertical' && [
          '[&>button]:border-b-0 [&>button]:border-r',
          '[&>button:first-child]:rounded-t-lg [&>button:first-child]:rounded-l-none',
          '[&>button:last-child]:rounded-b-lg [&>button:last-child]:rounded-r-none [&>button:last-child]:border-b'
        ],
        className
      )}
    >
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child) && child.type === Button) {
          return React.cloneElement(child, { size, variant } as ButtonProps);
        }
        return child;
      })}
    </div>
  );
};

// Icon Button Component
export interface IconButtonProps extends Omit<ButtonProps, 'children' | 'leftIcon' | 'rightIcon'> {
  icon: React.ReactNode;
  'aria-label': string;
}

export const IconButton: React.FC<IconButtonProps> = ({
  icon,
  size = 'md',
  className,
  ...props
}) => {
  const iconOnlyClasses = {
    xs: 'p-1.5',
    sm: 'p-2',
    md: 'p-2.5',
    lg: 'p-3',
    xl: 'p-3.5'
  };

  return (
    <Button
      size={size}
      className={cn(iconOnlyClasses[size], className)}
      {...props}
    >
      <span className={ICON_SIZES[size]}>
        {icon}
      </span>
    </Button>
  );
}; 